import { create } from 'zustand';
import { api } from '../utils/api';

export interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt: string;
  updatedAt?: string;
  parentId?: string;
}

export interface Chat {
  id: string;
  title: string;
  model: string;
  createdAt: string;
  updatedAt: string;
  isArchived: boolean;
  isPinned: boolean;
  messageCount?: number;
  lastMessage?: string;
}

interface ChatState {
  chats: Chat[];
  currentChat: Chat | null;
  messages: Message[];
  isLoading: boolean;
  isStreaming: boolean;
  streamingContent: string;
  error: string | null;
  
  // Actions
  fetchChats: () => Promise<void>;
  createChat: (title?: string) => Promise<Chat>;
  loadChat: (chatId: string) => Promise<void>;
  updateChat: (chatId: string, updates: Partial<Chat>) => Promise<void>;
  deleteChat: (chatId: string) => Promise<void>;
  archiveChat: (chatId: string) => Promise<void>;
  pinChat: (chatId: string, pinned: boolean) => Promise<void>;
  
  // Messages
  sendMessage: (content: string, stream?: boolean) => Promise<void>;
  addMessage: (message: Omit<Message, 'id' | 'createdAt'>) => void;
  updateMessage: (messageId: string, content: string) => void;
  deleteMessage: (messageId: string) => void;
  clearMessages: () => void;
  
  // Streaming
  setStreaming: (isStreaming: boolean) => void;
  appendStreamingContent: (content: string) => void;
  clearStreamingContent: () => void;
}

export const useChatStore = create<ChatState>((set, get) => ({
  chats: [],
  currentChat: null,
  messages: [],
  isLoading: false,
  isStreaming: false,
  streamingContent: '',
  error: null,

  fetchChats: async () => {
    try {
      const res: any = await api.get('/chat');
      set({ chats: res.chats || [] });
    } catch (error) {
      console.error('Failed to fetch chats:', error);
    }
  },

  createChat: async (title?: string) => {
    try {
      const res: any = await api.post('/chat', { title });
      const newChat = res.chat;
      set((state) => ({ 
        chats: [newChat, ...state.chats],
        currentChat: newChat,
        messages: []
      }));
      return newChat;
    } catch (error) {
      console.error('Failed to create chat:', error);
      throw error;
    }
  },

  loadChat: async (chatId: string) => {
    set({ isLoading: true, error: null });
    try {
      const res: any = await api.get(`/chat/${chatId}`);
      set({ 
        currentChat: res.chat,
        messages: res.messages || [],
        isLoading: false 
      });
    } catch (error) {
      set({ error: 'Failed to load chat', isLoading: false });
    }
  },

  updateChat: async (chatId: string, updates: Partial<Chat>) => {
    try {
      const res: any = await api.patch(`/chat/${chatId}`, updates);
      set((state) => ({
        chats: state.chats.map(c => c.id === chatId ? { ...c, ...res.chat } : c),
        currentChat: state.currentChat?.id === chatId ? { ...state.currentChat, ...res.chat } : state.currentChat
      }));
    } catch (error) {
      console.error('Failed to update chat:', error);
    }
  },

  deleteChat: async (chatId: string) => {
    try {
      await api.delete(`/chat/${chatId}`);
      set((state) => ({
        chats: state.chats.filter(c => c.id !== chatId),
        currentChat: state.currentChat?.id === chatId ? null : state.currentChat,
        messages: state.currentChat?.id === chatId ? [] : state.messages
      }));
    } catch (error) {
      console.error('Failed to delete chat:', error);
    }
  },

  archiveChat: async (chatId: string) => {
    await get().updateChat(chatId, { isArchived: true });
    set((state) => ({
      chats: state.chats.filter(c => c.id !== chatId)
    }));
  },

  pinChat: async (chatId: string, pinned: boolean) => {
    await get().updateChat(chatId, { isPinned: pinned });
  },

  sendMessage: async (content: string, stream = true) => {
    const { currentChat } = get();
    if (!currentChat) return;

    set({ isStreaming: stream, streamingContent: '', error: null });

    try {
      if (stream) {
        // Streaming request
        const response = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/ai/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('legion-auth') ? JSON.parse(localStorage.getItem('legion-auth')!).state.token : ''}`
          },
          body: JSON.stringify({
            chatId: currentChat.id,
            message: content,
            stream: true
          })
        });

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let fullContent = '';

        if (reader) {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value);
            const lines = chunk.split('\n');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));
                  if (data.type === 'chunk') {
                    fullContent += data.content;
                    set({ streamingContent: fullContent });
                  } else if (data.type === 'done') {
                    // Streaming complete
                  }
                } catch (e) {
                  // Ignore parse errors
                }
              }
            }
          }
        }

        // Refresh messages
        await get().loadChat(currentChat.id);
      } else {
        // Non-streaming request
        const res: any = await api.post('/ai/chat', {
          chatId: currentChat.id,
          message: content,
          stream: false
        });

        set((state) => ({
          messages: [...state.messages, res.message]
        }));
      }
    } catch (error) {
      set({ error: 'Failed to send message' });
    } finally {
      set({ isStreaming: false, streamingContent: '' });
    }
  },

  addMessage: (message) => {
    const newMessage: Message = {
      ...message,
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString()
    };
    set((state) => ({
      messages: [...state.messages, newMessage]
    }));
  },

  updateMessage: (messageId: string, content: string) => {
    set((state) => ({
      messages: state.messages.map(m => 
        m.id === messageId ? { ...m, content, updatedAt: new Date().toISOString() } : m
      )
    }));
  },

  deleteMessage: (messageId: string) => {
    set((state) => ({
      messages: state.messages.filter(m => m.id !== messageId)
    }));
  },

  clearMessages: () => set({ messages: [] }),

  setStreaming: (isStreaming: boolean) => set({ isStreaming }),
  appendStreamingContent: (content: string) => set((state) => ({ 
    streamingContent: state.streamingContent + content 
  })),
  clearStreamingContent: () => set({ streamingContent: '' }),
}));
